import { makeStyles, mergeClasses, tokens, Link } from '@fluentui/react-components';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { useAuth } from 'react-oidc-context';

import charBubbleArrowDisabled from '../../assets/bot-icons/chat-bubble-tip-disabled.svg';
import charBubbleArrow from '../../assets/bot-icons/chat-bubble-tip.svg';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import {
    addMessageToConversationFromServer,
    cancelInFlightRequest,
    editConversationInput,
} from '../../redux/features/conversations/conversationsSlice';
import { useTypingEmitter } from '../../redux/features/message-relay/useTypingEmitter';
import { addAlert } from '../../redux/features/app/appSlice';
import { AlertType } from '../../libs/models/AlertType';
import { useCallbackQueueOpen } from '../../libs/hooks/useCallbackQueueOpen';
import {
    AuthorRoles,
    currentQueueTimeoutIndex,
    isAgentSessionActive,
    isChatClosedAfterHandover,
} from '../../libs/models/ChatMessage';
import { ChatService } from '../../libs/services/ChatService';
import { CustomerLiveChatService } from '../../libs/services/CustomerLiveChatService';
import { IAskImage } from '../../libs/semantic-kernel/model/Ask';
import {
    clipboardEventToBlob,
    downscaleImage,
    ImageTooLargeError,
    SupportedImageMime,
    UnsupportedImageFormatError,
} from '../../libs/utils/imageUtils';
import { buildLocalImageEcho } from '../../libs/utils/localImageEcho';
import { isAllowedByPermissionsPolicy } from '../../libs/utils/permissionsPolicy';
import { appendDictation } from '../../libs/voice/dictationText';
import { Constants } from '../../Constants';
import { Alerts } from '../shared/Alerts';
import { ChatStatus } from './ChatStatus';
import { ScreenshotRequestPrompt } from './ScreenshotRequestPrompt';
import { clearScreenshotRequest, ScreenshotRequestEntry } from '../../redux/features/livechat/screenshotRequestSlice';
import Icon from '../utils/Icon';
import { Stop24Filled } from '../shared/BundledIcons';
import { CallbackModal } from './callback/CallbackModal';
import { DictationButton } from './DictationButton';
import { VoiceButton } from './VoiceButton';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    rootEmbedded: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    typingIndicator: {
        maxHeight: '28px',
        width: '60%',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    content: {
        position: 'relative',
        gap: tokens.spacingHorizontalM,
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    },
    // Room for dictation, voice conversation and send.
    textareaWithVoice: {
        paddingRight: '140px',
    },
    TextareaAutosize: {
        padding: '8px 70px 8px 15px',
        overflow: 'hidden',
        border: `1px solid ${tokens.colorNeutralForeground3}`,
        borderRadius: '10px',
        outline: '0',
        width: '100%',
        resize: 'none',
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        '&::placeholder': {
            color: tokens.colorNeutralForeground2,
        },
        '&:focus': {
            borderTopColor: tokens.colorBrandForeground1,
            borderRightColor: tokens.colorBrandForeground1,
            borderBottomColor: tokens.colorBrandForeground1,
            borderLeftColor: tokens.colorBrandForeground1,
            outline: 'none',
        },
    },
    textareaDisabled: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderTopColor: tokens.colorNeutralStrokeDisabled,
        borderRightColor: tokens.colorNeutralStrokeDisabled,
        borderBottomColor: tokens.colorNeutralStrokeDisabled,
        borderLeftColor: tokens.colorNeutralStrokeDisabled,
        '&::placeholder': {
            color: tokens.colorNeutralForegroundDisabled,
        },
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto',
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
    },
    dragAndDrop: {
        padding: '8px',
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },
    userIcon: {
        alignSelf: 'flex-end',
        marginRight: '1rem',
        marginBottom: '10.5px',
    },
    arrow: {
        alignSelf: 'flex-end',
        backgroundImage: `url(${charBubbleArrow})`,
        height: '40px',
        width: '40px',
        backgroundRepeat: 'no-repeat',
        position: 'absolute',
        left: '-10px',
        marginBottom: '10.5px',
        zIndex: 1,
    },
    arrowDisabled: {
        backgroundImage: `url(${charBubbleArrowDisabled})`,
    },
    sendButton: {
        alignSelf: 'flex-end',
        position: 'absolute',
        right: '9.5px',
        top: '50%',
        transform: 'translateY(-50%)',
        maxWidth: 'none',
        width: '40px',
        height: '40px',
        minWidth: 'unset',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        '&:focus-visible': {
            outline: 'none',
            border: `2px solid ${tokens.colorBrandForegroundLink}`,
            borderRadius: '8px',
        },
        '&:disabled': {
            cursor: 'not-allowed',
        },
    },
    sendButtonEmpty: {
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    sendButtonActive: {
        backgroundColor: tokens.colorBrandBackground,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: tokens.colorBrandBackgroundHover,
        },
    },
    inputWrapper: {
        display: 'flex',
        flexDirection: 'row',
        height: 'auto',
        maxWidth: '900px',
        width: '100%',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    inputWrapperEmbedded: {
        display: 'flex',
        flexDirection: 'row',
        height: 'auto',
        width: '100%',
    },
    disclaimer: {
        fontSize: '10px',
        color: tokens.colorNeutralForeground2,
        maxWidth: '900px',
        width: '100%',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    callbackLink: {
        textAlign: 'right',
        marginBottom: tokens.spacingVerticalS,
        maxWidth: '900px',
        width: '100%',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    pasteHint: {
        fontSize: '11px',
        color: tokens.colorNeutralForeground3,
        marginTop: tokens.spacingVerticalXS,
        maxWidth: '900px',
        width: '100%',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    thumbnailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        marginBottom: tokens.spacingVerticalXS,
        maxWidth: '900px',
        width: '100%',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    thumbnailWrapper: {
        position: 'relative',
        display: 'inline-block',
    },
    thumbnailImage: {
        maxHeight: '64px',
        maxWidth: '120px',
        borderRadius: '8px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        objectFit: 'cover',
    },
    thumbnailRemoveButton: {
        position: 'absolute',
        top: '-6px',
        right: '-6px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        fontSize: '12px',
        lineHeight: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    thumbnailHint: {
        fontSize: '11px',
        color: tokens.colorNeutralForeground3,
    },
});

interface PendingImage {
    base64: string;
    mimeType: SupportedImageMime;
    previewUrl: string;
}

interface ChatInputProps {
    handleSubmit: (text: string, messageType?: undefined, image?: IAskImage) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({ handleSubmit }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { DynamicReport, VoiceModeEnabled } = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags,
    );
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const activeUserInfo = useAppSelector((state: RootState) => state.app.activeUserInfo);

    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const messages = conversations[selectedId].messages;
    const auth = useAuth();
    const token = auth.user?.access_token;

    const [value, setValue] = useState('');
    const [historyIndex, setHistoryIndex] = useState(-1);
    // What the user was typing before ArrowUp took them into history, so ArrowDown past the newest
    // message brings it back.
    const draftBeforeHistory = useRef('');
    const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
    const [pendingImage, setPendingImage] = useState<PendingImage | null>(null);
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const imageBusy = isProcessingImage || isUploadingImage;
    // Brief cooldown after a cancel click so the user can't immediately Send another
    // request and trigger the workflow-wins-the-race scenario (#1554).
    const [postCancelCooldown, setPostCancelCooldown] = useState(false);
    const cancelCooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (cancelCooldownTimer.current) {
                clearTimeout(cancelCooldownTimer.current);
            }
        };
    }, []);

    // Local image echoes (empty content, or the prompt's caption) are not something the customer
    // typed, and history relies on a recalled message never being empty.
    const userMessages = useMemo(
        () =>
            messages
                .filter((msg) => msg.authorRole === AuthorRoles.User && !msg.isLocalEcho && msg.content !== '')
                .map((msg) => msg.content),
        [messages],
    );

    // Gates the customer-side typing emitter so it stays silent during a pure-AI conversation.
    const isAgentHandling = useMemo(() => isAgentSessionActive(messages), [messages]);

    // Lock the composer once the handover is resolved; the transcript stays readable (#1938).
    const isChatClosed = useMemo(() => isChatClosedAfterHandover(messages), [messages]);

    const { onType: emitTyping, stop: stopTyping } = useTypingEmitter(selectedId, isAgentHandling);

    // The server refuses voice while a human handles the chat, so the button would only open the microphone
    // prompt for an error. Hiding it mid-session unmounts it, which stops the session.
    // The flag is switched in App Configuration without a deploy, so it can be on while the page's policy
    // blocks the microphone (prd until the voice rollout, #2580, or a host iframe without allow="microphone").
    const showVoice =
        VoiceModeEnabled && !isChatClosed && !isAgentHandling && isAllowedByPermissionsPolicy('microphone');
    // Dictation and a voice conversation both hold the microphone, and the conversation would hear the dictated
    // question as one of its own: only one of them at a time.
    const [conversing, setConversing] = useState(false);
    const [dictating, setDictating] = useState(false);

    // Agent's pending screenshot request for this chat. Only meaningful while a human agent
    // is handling the chat; a request that outlives the handover is dropped rather than left dangling.
    const screenshotRequested = useAppSelector(
        (state: RootState) => state.screenshotRequest.requests[selectedId] as ScreenshotRequestEntry | undefined,
    );
    const showScreenshotPrompt = !!screenshotRequested && isAgentHandling && !isChatClosed;
    useEffect(() => {
        if (screenshotRequested && (!isAgentHandling || isChatClosed)) {
            dispatch(clearScreenshotRequest({ chatId: selectedId }));
        }
    }, [screenshotRequested, isAgentHandling, isChatClosed, selectedId, dispatch]);

    const isTimeoutOfferShown = useMemo(() => currentQueueTimeoutIndex(messages) !== -1, [messages]);

    const MIN_MESSAGES_FOR_CALLBACK = 7;
    const isCallbackQueueOpen = useCallbackQueueOpen();
    // Suppress the phone-callback offer during and after a human handover: customers read "Would you
    // like us to call you?" as the live agent offering to phone them, which it isn't. isChatClosed is
    // kept separate from the composer lock because the link renders independently of the input.
    // Right after a queue timeout the same offer sits under the timeout notice instead.
    const shouldShowCallbackLink =
        messages.length >= MIN_MESSAGES_FOR_CALLBACK &&
        isCallbackQueueOpen &&
        !isAgentHandling &&
        !isChatClosed &&
        !isTimeoutOfferShown;

    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        // Focus on the text area when the component mounts / page loads
        textAreaRef.current?.focus();
    }, [textAreaRef]);

    useEffect(() => {
        // Focus on the text area when the selected conversation changes
        textAreaRef.current?.focus();
        // Leave history mode too. This conversation's stored input may be one of its own recalled
        // messages, which can make the previous conversation's index look valid here and let ArrowDown
        // restore that conversation's draft.
        setHistoryIndex(-1);
    }, [selectedId]);

    const {
        disabled: chatStateDisabled,
        input: chatStateInput,
        botResponseStatus,
    } = useMemo(() => conversations[selectedId], [conversations, selectedId]);
    const inputDisabled = chatStateDisabled || isChatClosed;
    // An image alone is sendable while an agent handles the chat; the AI path still needs text.
    const hasSendableContent = Boolean(value.trim()) || (pendingImage !== null && isAgentHandling);
    // Not while a dictation runs: what is still being said or written out would come after the send, as a draft of its own.
    const canSend = hasSendableContent && !inputDisabled && !imageBusy && !postCancelCooldown && !dictating;
    const isStopButtonVisible = botResponseStatus !== undefined;

    const handleStopClick = () => {
        dispatch(cancelInFlightRequest({ chatId: selectedId }));
        setPostCancelCooldown(true);
        if (cancelCooldownTimer.current) {
            clearTimeout(cancelCooldownTimer.current);
        }
        cancelCooldownTimer.current = setTimeout(() => {
            setPostCancelCooldown(false);
        }, Constants.CANCEL_COOLDOWN_MS);
        if (!token) return;
        const chatService = new ChatService(token);
        void chatService.cancelChatAsync(selectedId, hostContext);
    };

    useEffect(() => {
        setValue(inputDisabled ? '' : chatStateInput);

        // Sending ends the trip through history. If Stop then puts a recalled message back, the field
        // matches its history entry again and ArrowDown would swap it for the draft from before. Every
        // send clears the stored input, whichever component sent it (the suggestion buttons send through
        // ChatRoom, not handleSubmitWrapper) and also during a human handover, when the input is not
        // disabled. A recalled message is never empty, so an empty input always means history is over.
        if (chatStateInput === '') {
            setHistoryIndex(-1);
        }

        // Defer focus to the next tick so that React has flushed the new `value` to the
        // DOM before `onFocus` reads it. Focusing inline would otherwise let onFocus see
        // the stale (pre-setValue) DOM value and overwrite our restore with '' — the bug
        // that wiped the cancelled prompt out of the input on cancel (#1554).
        if (!inputDisabled) {
            const timer = setTimeout(() => {
                textAreaRef.current?.focus();
            }, 0);
            return () => {
                clearTimeout(timer);
            };
        }
        return undefined;
    }, [inputDisabled, chatStateInput]);

    // Through a ref, as the dictation finishes after an await: the input by then may hold what the user typed meanwhile.
    const valueRef = useRef(value);
    valueRef.current = value;
    const onDictated = useCallback(
        (text: string) => {
            const next = appendDictation(valueRef.current, text);
            setValue(next);
            dispatch(editConversationInput({ id: selectedId, newInput: next }));
            // Deferred as in the input effect above (#1554): focused now, onFocus would read the DOM before React has
            // written `next` to it and put the old draft back.
            setTimeout(() => {
                textAreaRef.current?.focus();
            }, 0);
        },
        [dispatch, selectedId],
    );

    const updateInputWithHistory = (newIndex: number) => {
        setHistoryIndex(newIndex);
        const historicalInput = newIndex === -1 ? draftBeforeHistory.current : userMessages[newIndex];
        setValue(historicalInput);
        dispatch(editConversationInput({ id: selectedId, newInput: historicalInput }));
    };

    const navigateHistory = (direction: 'up' | 'down') => {
        // History mode lasts only while the field still shows the recalled message. An edit, a send or
        // an outside write (e.g. the prompt put back after Stop) ends it by itself, so that text becomes
        // the draft instead of being overwritten, and a stale index is never used to read userMessages.
        const inHistory = historyIndex >= 0 && value === userMessages[historyIndex];
        if (direction === 'up' && userMessages.length > 0) {
            if (!inHistory) {
                draftBeforeHistory.current = value;
            }
            const newIndex = inHistory ? Math.max(0, historyIndex - 1) : userMessages.length - 1;
            updateInputWithHistory(newIndex);
        } else if (direction === 'down' && inHistory) {
            const newIndex = historyIndex + 1;
            updateInputWithHistory(newIndex >= userMessages.length ? -1 : newIndex);
        }
    };

    const clearPendingImage = () => {
        if (pendingImage) {
            URL.revokeObjectURL(pendingImage.previewUrl);
        }
        setPendingImage(null);
    };

    // Cleanup the object URL when the component unmounts or when a new image replaces an old one.
    useEffect(() => {
        return () => {
            if (pendingImage) {
                URL.revokeObjectURL(pendingImage.previewUrl);
            }
        };
    }, [pendingImage]);

    const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const blob = clipboardEventToBlob(e);
        if (!blob) return; // not an image — let the native paste flow run

        e.preventDefault();
        if (isUploadingImage) return;
        setIsProcessingImage(true);
        try {
            // Routing is decided at send time, and a paste made while queued may go to a human agent
            // once one joins: always re-encode so no EXIF (GPS, device, time) can travel with it.
            const { base64, mimeType } = await downscaleImage(blob, undefined, { stripMetadata: true });
            const previewUrl = URL.createObjectURL(blob);
            // Replace any prior image — single-image-per-message in v1.
            if (pendingImage) {
                URL.revokeObjectURL(pendingImage.previewUrl);
            }
            setPendingImage({ base64, mimeType, previewUrl });
        } catch (err: unknown) {
            const messageKey =
                err instanceof ImageTooLargeError
                    ? 'chatInput.imageTooLarge'
                    : err instanceof UnsupportedImageFormatError
                      ? 'chatInput.imageUnsupportedFormat'
                      : 'chatInput.imagePasteFailed';
            dispatch(addAlert({ type: AlertType.Warning, message: t(messageKey) }));
        } finally {
            setIsProcessingImage(false);
        }
    };

    // While an agent handles the chat the AI request path drops any image (the orchestrator
    // short-circuits), so the image goes to the agent through the LiveChat upload instead and is
    // echoed locally the same way a pasted image is on the AI path.
    const sendImageToAgent = async (image: PendingImage): Promise<boolean> => {
        if (!token) {
            dispatch(addAlert({ type: AlertType.Warning, message: t('chatInput.imageSendFailed') }));
            return false;
        }
        setIsUploadingImage(true);
        const answersRequest = showScreenshotPrompt;
        try {
            await new CustomerLiveChatService(token).uploadScreenshotAsync(
                selectedId,
                { base64: image.base64, mimeType: image.mimeType, requested: answersRequest },
                hostContext,
            );
            dispatch(
                addMessageToConversationFromServer({
                    message: buildLocalImageEcho(selectedId, activeUserInfo, image.base64, image.mimeType, ''),
                    chatId: selectedId,
                }),
            );
            if (answersRequest) {
                dispatch(clearScreenshotRequest({ chatId: selectedId }));
            }
            return true;
        } catch {
            dispatch(addAlert({ type: AlertType.Warning, message: t('chatInput.imageSendFailed') }));
            return false;
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleSubmitWrapper = async (text: string) => {
        if (inputDisabled || isUploadingImage) {
            return;
        }
        const hasText = text.trim() !== '';
        if (pendingImage && isAgentHandling) {
            if (!(await sendImageToAgent(pendingImage))) {
                return;
            }
            clearPendingImage();
            if (!hasText) {
                return;
            }
            stopTyping();
            handleSubmit(text);
            return;
        }
        if (!hasText) {
            return;
        }
        const image = pendingImage ? { base64: pendingImage.base64, mimeType: pendingImage.mimeType } : undefined;
        // Clear the typing indicator immediately on send rather than waiting for the inactivity
        // timer, so the agent doesn't briefly see "kunden skriver…" alongside the delivered message.
        stopTyping();
        handleSubmit(text, undefined, image);
        clearPendingImage();
    };

    return (
        <div className={embeddedMode ? classes.rootEmbedded : classes.root}>
            {/* Pinned status slot above the composer. In DynamicReport mode it carries only the
                agent-typing row (#1933) — the AI statuses live in ChatStatusCard inside the message
                flow; in legacy mode it carries the agent-typing row plus the AI status line. */}
            <div className={classes.typingIndicator}>
                <ChatStatus agentTypingOnly={DynamicReport} />
            </div>
            <Alerts />
            {showScreenshotPrompt && (
                <ScreenshotRequestPrompt chatId={selectedId} token={token} hostContext={hostContext} />
            )}
            {shouldShowCallbackLink && (
                <div className={classes.callbackLink}>
                    <Link
                        appearance="subtle"
                        onClick={() => {
                            setIsCallbackModalOpen(true);
                        }}
                    >
                        {t('callback.callInsteadLink')}
                    </Link>
                </div>
            )}
            {pendingImage && (
                <div className={classes.thumbnailRow}>
                    <div className={classes.thumbnailWrapper}>
                        <img src={pendingImage.previewUrl} alt="" className={classes.thumbnailImage} />
                        <button
                            type="button"
                            aria-label={t('chatInput.imageThumbnailRemove')}
                            title={t('chatInput.imageThumbnailRemove')}
                            className={classes.thumbnailRemoveButton}
                            onClick={clearPendingImage}
                            disabled={imageBusy}
                        >
                            ✕
                        </button>
                    </div>
                    {imageBusy && <span className={classes.thumbnailHint}>{t('chatInput.imageProcessing')}</span>}
                </div>
            )}
            <div className={embeddedMode ? classes.inputWrapperEmbedded : classes.inputWrapper}>
                <div className={classes.content}>
                    <TextareaAutosize
                        minRows={3}
                        maxRows={10}
                        title={t('chatInput.textAreaTitle')}
                        aria-label={t('chatInput.textAreaAriaLabel')}
                        ref={textAreaRef}
                        id="chat-input"
                        rows={1}
                        placeholder={
                            isChatClosed
                                ? t('chatInput.chatClosed')
                                : messages.length > 1
                                  ? t('chatInput.replay')
                                  : t('chatInput.askAQuestion')
                        }
                        disabled={inputDisabled || isUploadingImage}
                        className={mergeClasses(
                            classes.TextareaAutosize,
                            showVoice ? classes.textareaWithVoice : undefined,
                            inputDisabled ? classes.textareaDisabled : undefined,
                        )}
                        value={value}
                        onFocus={() => {
                            // update the locally stored value to the current value
                            const chatInput = document.getElementById('chat-input');
                            if (chatInput) {
                                setValue((chatInput as HTMLTextAreaElement).value);
                            }
                        }}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            setValue(e.target.value);
                            dispatch(editConversationInput({ id: selectedId, newInput: e.target.value }));
                            emitTyping();
                        }}
                        onPaste={(event) => {
                            void handlePaste(event);
                        }}
                        onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                if (!canSend) return;
                                void handleSubmitWrapper(value);
                            } else if (event.key === 'ArrowUp' && !event.shiftKey) {
                                event.preventDefault();
                                navigateHistory('up');
                            } else if (event.key === 'ArrowDown' && !event.shiftKey) {
                                event.preventDefault();
                                navigateHistory('down');
                            }
                        }}
                    />
                    {showVoice && (
                        <DictationButton
                            chatId={selectedId}
                            disabled={inputDisabled || isUploadingImage || conversing}
                            onText={onDictated}
                            onActiveChange={setDictating}
                        />
                    )}
                    {showVoice && (
                        <VoiceButton chatId={selectedId} disabled={dictating} onActiveChange={setConversing} />
                    )}
                    {isStopButtonVisible ? (
                        <button
                            type="button"
                            title={t('chatInput.stopButtonTitle')}
                            aria-label={t('chatInput.stopButtonAriaLabel')}
                            className={mergeClasses(classes.sendButton, classes.sendButtonActive)}
                            onClick={handleStopClick}
                        >
                            <Stop24Filled
                                style={{
                                    color: 'var(--app-send-button-active-icon-color)',
                                    width: 18,
                                    height: 18,
                                }}
                            />
                        </button>
                    ) : (
                        (() => {
                            const iconColor = canSend
                                ? 'var(--app-send-button-active-icon-color)'
                                : tokens.colorNeutralForegroundDisabled;

                            const buttonClassName = mergeClasses(
                                classes.sendButton,
                                canSend ? classes.sendButtonActive : classes.sendButtonEmpty,
                            );

                            return (
                                <button
                                    type="button"
                                    title={t('chatInput.submitButtonTitle')}
                                    aria-label={t('chatInput.submitButtonAriaLabel')}
                                    className={buttonClassName}
                                    onClick={() => {
                                        void handleSubmitWrapper(value);
                                    }}
                                    disabled={!canSend}
                                >
                                    <Icon name="paperplane" size={16} style={{ color: iconColor }} />
                                </button>
                            );
                        })()
                    )}
                </div>
            </div>
            {!value && !pendingImage && messages.length <= 1 && (
                <div className={classes.pasteHint}>{t('chatInput.pasteImageTip')}</div>
            )}
            <div className={classes.disclaimer}>{t('chatInput.disclaimer')}</div>

            <CallbackModal isOpen={isCallbackModalOpen} onOpenChange={setIsCallbackModalOpen} />
        </div>
    );
};
